import { pairsRequestAction, recommendationsUrl } from './lf-utils.js';

/**
 * "Pairs well with" in the cart drawer and on the cart page: two recommended products for the first line item.
 *
 * Nothing is fetched on page load. In the drawer the request waits for `theme-drawer:open`; on the cart page it waits until the
 * element is near the viewport. After that the cart section is morphed by Horizon on every cart change, which updates
 * `data-product-id` (the first line item) and `data-cart-products`; a change to either re-requests while the drawer is open
 * (or the page element is on screen) and is picked up on the next open otherwise. The body is marked `data-skip-node-update`
 * so Horizon's morph keeps the fetched rows and the hidden state.
 *
 * Shopify's recommendations endpoint answers with the `lf-cart-pairs` section (Section Rendering). The response is parsed into
 * a document and the `<li data-lf-pair>` rows are imported node by node, never inserted as an HTML string. Each row is
 * Horizon's own product-card with its quick-add, so adding a product goes through Horizon's cart code and the drawer updates.
 */
class LfCartPairs extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-cart-products'];

  /** @type {AbortController | null} */
  #abort = null;
  #loadedKey = '';
  #pendingKey = '';
  #queued = false;
  #collapseWhenHidden = false;
  #visible = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {HTMLStyleElement | null} */
  #style = null;
  #statusTimer = 0;
  /** @type {HTMLElement | null} */
  #bodyForReservation = null;

  get #drawer() {
    return this.closest('theme-drawer');
  }

  get #active() {
    const drawer = this.#drawer;
    return drawer ? drawer.hasAttribute('open') : this.#visible;
  }

  connectedCallback() {
    this.addEventListener('shopify:cart:lines-update', this.#onLinesUpdate);
    if (this.#drawer) {
      document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
      if (this.#active) this.#schedule();
    } else if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '240px 0px' });
      this.#observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('shopify:cart:lines-update', this.#onLinesUpdate);
    window.clearTimeout(this.#statusTimer);
    document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {string} name
   * @param {string | null} previous
   * @param {string | null} next
   */
  attributeChangedCallback(name, previous, next) {
    if (previous === next || !this.isConnected) return;
    if (this.#active) this.#schedule();
  }

  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    if (event.target === this.#drawer) this.#schedule();
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    this.#visible = entries.some((entry) => entry.isIntersecting);
    if (this.#visible) this.#schedule();
    else if (this.#collapseWhenHidden) this.#collapseReservation();
  };

  /**
   * Announces a one-tap add. The status line sits beside the replaced rows, never inside them, so it survives the refresh
   * that follows (the added product leaves the list and the focused button is replaced).
   * @param {Event} event
   */
  #onLinesUpdate = (event) => {
    const update = /** @type {{ action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} */ (/** @type {unknown} */ (event));
    if (update.action !== 'add' || !update.promise) return;
    update.promise
      .then((result) => {
        if (!result?.detail?.didError) this.#announce('Added to cart');
      })
      .catch(() => {});
  };

  /** @param {string} text */
  #announce(text) {
    const status = this.querySelector('[data-lf-pairs-status]');
    if (!status) return;
    window.clearTimeout(this.#statusTimer);
    status.textContent = '';
    this.#statusTimer = window.setTimeout(() => {
      status.textContent = text;
      this.#statusTimer = window.setTimeout(() => {
        status.textContent = '';
      }, 5000);
    }, 60);
  }

  /**
   * Replacing the rows destroys the button that had focus. Put focus on the first new Add (or Choose) button; with no rows left
   * the section is hidden, so focus goes to the gift note control just above it (or the drawer's close button).
   * @param {HTMLElement} body
   * @param {Element} list
   */
  #restoreFocus(body, list) {
    const buttons = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('button.quick-add__button')]);
    const target =
      buttons.find((button) => button.getClientRects().length > 0) ??
      (!body.hidden ? body.querySelector('.lf-pairs__title') : null) ??
      this.closest('.lf-cart-extras')?.querySelector('.lf-cart-note__summary') ??
      this.closest('theme-drawer')?.querySelector('.theme-drawer__close-button') ??
      this.#pageFallbackTarget();
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /** On the cart page there may be no close button or gift note control to land on; the title (or the main region) is the last resort. */
  #pageFallbackTarget() {
    const target = document.querySelector('main h1') ?? document.getElementById('MainContent');
    if (!(target instanceof HTMLElement)) return null;
    if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
    return target;
  }

  /**
   * On the cart page the section reserves its height in CSS so arriving rows never push the page down. With nothing to show the
   * reservation is released only while the section is off screen (at once if it is, else when the observer says it left; at
   * once without an observer).
   * @param {HTMLElement} body
   * @param {boolean} hasRows
   */
  #settleReservation(body, hasRows) {
    this.#bodyForReservation = body;
    this.#collapseWhenHidden = false;
    if (this.#drawer) return;
    if (hasRows) {
      body.removeAttribute('data-lf-collapsed');
      return;
    }
    const rect = this.getBoundingClientRect();
    const offScreen = rect.top > window.innerHeight || rect.bottom < 0;
    if (offScreen || !this.#observer) body.setAttribute('data-lf-collapsed', '');
    else this.#collapseWhenHidden = true;
  }

  #collapseReservation() {
    this.#collapseWhenHidden = false;
    this.#bodyForReservation?.setAttribute('data-lf-collapsed', '');
  }

  /** Coalesces the two attribute changes Horizon's morph makes in one go into a single request. */
  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      void this.#refresh();
    });
  }

  async #refresh() {
    const body = this.querySelector('[data-lf-pairs-body]');
    const list = body?.querySelector('[data-lf-pairs-list]');
    if (!(body instanceof HTMLElement) || !list) return;

    const key = `${this.dataset.productId ?? ''}|${this.dataset.cartProducts ?? ''}`;
    const action = pairsRequestAction({ key, loadedKey: this.#loadedKey, pendingKey: this.#pendingKey });
    if (action === 'cancel') {
      this.#abort?.abort();
      this.#abort = null;
      this.#pendingKey = '';
      return;
    }
    if (action === 'skip') return;

    const url = recommendationsUrl(
      this.dataset.url,
      { productId: this.dataset.productId, sectionId: this.dataset.sectionId },
      window.location.origin,
    );
    if (!url) {
      body.hidden = true;
      this.#settleReservation(body, false);
      return;
    }

    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;
    this.#pendingKey = key;

    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: abort.signal });
      if (!response.ok) throw new Error(`Recommendations returned ${response.status}`);

      const incoming = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (abort.signal.aborted || !this.isConnected) return;

      const rows = [...incoming.querySelectorAll('[data-lf-pair]')].slice(0, 2).map((row) => document.importNode(row, true));
      const stylesheet = incoming.querySelector('style[data-section-stylesheet]');

      this.#style?.remove();
      this.#style = null;
      if (rows.length > 0 && stylesheet instanceof HTMLStyleElement) {
        this.#style = /** @type {HTMLStyleElement} */ (document.importNode(stylesheet, true));
        body.append(this.#style);
      }

      const hadFocus = list.contains(document.activeElement);
      list.replaceChildren(...rows);
      body.hidden = rows.length === 0;
      this.#loadedKey = key;
      this.#settleReservation(body, rows.length > 0);
      if (hadFocus) this.#restoreFocus(body, list);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      body.hidden = true;
      this.#settleReservation(body, false);
      console.warn('[lf-cart-pairs] Could not load recommendations:', error);
    } finally {
      if (this.#abort === abort) {
        this.#abort = null;
        this.#pendingKey = '';
      }
    }
  }
}

if (!customElements.get('lf-cart-pairs')) customElements.define('lf-cart-pairs', LfCartPairs);
